import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type { Enums, Tables } from "@/types";

export type Lead = Tables<"leads">;
export type LeadStatus = Enums<"lead_status">;
export type LeadSource = Enums<"lead_source">;

export const LEAD_STATUS_LABEL: Record<LeadStatus, string> = {
  received: "Disponible", review: "À revoir", assigned: "Attribué", delivered: "Livré", disputed: "Contesté", replaced: "Remplacé", credited: "Crédité", rejected: "Rejeté",
};
export const LEAD_STATUS_VARIANT: Record<LeadStatus, "default" | "secondary" | "destructive" | "outline"> = {
  received: "outline", review: "destructive", assigned: "secondary", delivered: "default", disputed: "destructive", replaced: "secondary", credited: "secondary", rejected: "outline",
};
export const LEAD_SOURCE_LABEL: Record<LeadSource, string> = { manual: "Saisie", csv: "CSV", airtable: "Airtable", meta: "Meta", google: "Google", api: "API" };

export type LeadRow = Lead & {
  lead_identities: { id: string; first_name: string | null; last_name: string | null; phone: string | null; email: string | null; anonymized_at: string | null } | null;
  products: { sku: string; name: string } | null;
  clients: { id: string; legal_name: string } | null;
};

export function useLeads(opts: { status?: string; clientId?: string; search?: string; review?: boolean } = {}) {
  return useQuery({
    queryKey: ["leads", opts],
    queryFn: async () => {
      let q = supabase.from("leads").select("*, lead_identities(id, first_name, last_name, phone, email, anonymized_at), products(sku, name), clients!leads_client_id_fkey(id, legal_name)").order("received_at", { ascending: false }).limit(500);
      if (opts.review) q = q.eq("review_status", "pending");
      else if (opts.status && opts.status !== "all") q = q.eq("status", opts.status as LeadStatus);
      if (opts.clientId) q = q.eq("client_id", opts.clientId);
      const { data, error } = await q; if (error) throw error;
      let rows = data as unknown as LeadRow[];
      const s = opts.search?.trim().toLowerCase();
      if (s) rows = rows.filter((l) => [l.lead_identities?.first_name, l.lead_identities?.last_name, l.lead_identities?.phone, l.lead_identities?.email, l.zone, l.external_ref].some((v) => v?.toLowerCase().includes(s)));
      return rows;
    },
  });
}

export function useIdentityHistory(identityId: string | undefined) {
  return useQuery({
    queryKey: ["identity_history", identityId], enabled: Boolean(identityId),
    queryFn: async () => { const { data, error } = await supabase.rpc("identity_history", { _identity_id: identityId! }); if (error) throw error; return data; },
  });
}

export function useAssignableItems(productId: string | null | undefined, leadId?: string) {
  return useQuery({
    queryKey: ["assignable_items", productId ?? null, leadId ?? null],
    queryFn: async () => { const { data, error } = await supabase.rpc("assignable_order_items", { _product_id: productId ?? undefined, _lead_id: leadId }); if (error) throw error; return data; },
  });
}

function invalidateLeads(qc: ReturnType<typeof useQueryClient>) {
  for (const k of ["leads", "identity_history", "assignable_items", "order", "orders", "deliveries", "disputes", "dispute", "dashboard"]) void qc.invalidateQueries({ queryKey: [k] });
}
const msg = (e: unknown, fallback: string) => ((e as { message?: string }).message ?? fallback).replace(/^.*?:\s*(?=[A-ZÀ-Ü])/, "");

export function useLeadActions() {
  const qc = useQueryClient();
  const done = (t: string) => () => { invalidateLeads(qc); toast.success(t); };
  const fail = (op: string) => (e: unknown) => { toast.error(msg(e, "Action impossible.")); logger.error(op, e); };
  return {
    importLeads: useMutation({
      mutationFn: async (input: { rows: Record<string, unknown>[]; source: LeadSource }) => { const { data, error } = await supabase.rpc("import_leads", { _rows: input.rows as never, _source: input.source }); if (error) throw error; return data as { imported: number; review: number; duplicates: number; errors: { row: number; error: string }[] }; },
      onSuccess: () => invalidateLeads(qc), onError: fail("leads.import"),
    }),
    assign: useMutation({
      mutationFn: async (input: { leadId: string; orderItemId: string }) => { const { error } = await supabase.rpc("assign_lead", { _lead_id: input.leadId, _order_item_id: input.orderItemId }); if (error) throw error; },
      onSuccess: done("Lead attribué"), onError: fail("lead.assign"),
    }),
    review: useMutation({
      mutationFn: async (input: { leadId: string; decision: "same_client" | "merged" | "release"; note: string }) => { const { error } = await supabase.rpc("review_lead", { _lead_id: input.leadId, _decision: input.decision, _note: input.note }); if (error) throw error; },
      onSuccess: done("Décision enregistrée"), onError: fail("lead.review"),
    }),
    deliver: useMutation({
      mutationFn: async (input: { orderId: string; leadIds: string[]; channel: Enums<"delivery_channel">; reference: string; note: string; deliveredAt?: string }) => {
        const { data, error } = await supabase.rpc("record_delivery", { _order_id: input.orderId, _lead_ids: input.leadIds, _channel: input.channel, _reference: input.reference, _note: input.note, _delivered_at: input.deliveredAt });
        if (error) throw error; return data as string;
      },
      onSuccess: done("Livraison enregistrée"), onError: fail("delivery.record"),
    }),
    openDispute: useMutation({
      mutationFn: async (input: { orderId: string; leadIds: string[]; reason: string }) => { const { data, error } = await supabase.rpc("open_dispute", { _order_id: input.orderId, _lead_ids: input.leadIds, _reason: input.reason }); if (error) throw error; return data as string; },
      onSuccess: done("Contestation ouverte"), onError: fail("dispute.open"),
    }),
    decide: useMutation({
      mutationFn: async (input: { disputeLeadId: string; decision: "rejected" | "replaced" | "credited"; note: string; replacementLeadId?: string }) => { const { error } = await supabase.rpc("decide_dispute_lead", { _dispute_lead_id: input.disputeLeadId, _decision: input.decision, _note: input.note, _replacement_lead_id: input.replacementLeadId }); if (error) throw error; },
      onSuccess: done("Décision enregistrée"), onError: fail("dispute.decide"),
    }),
    resolve: useMutation({
      mutationFn: async (input: { disputeId: string; note: string }) => { const { error } = await supabase.rpc("resolve_dispute", { _dispute_id: input.disputeId, _note: input.note }); if (error) throw error; },
      onSuccess: done("Contestation close"), onError: fail("dispute.resolve"),
    }),
    anonymize: useMutation({
      mutationFn: async (input: { identityId: string; reason: string }) => { const { error } = await supabase.rpc("anonymize_identity", { _identity_id: input.identityId, _reason: input.reason }); if (error) throw error; },
      onSuccess: done("Identité anonymisée"), onError: fail("identity.anonymize"),
    }),
  };
}

export function useDeliveries(orderId: string | undefined) {
  return useQuery({
    queryKey: ["deliveries", orderId], enabled: Boolean(orderId),
    queryFn: async () => { const { data, error } = await supabase.from("deliveries").select("*").eq("order_id", orderId!).order("delivered_at", { ascending: false }); if (error) throw error; return data; },
  });
}
export function useOrderLeads(orderId: string | undefined) {
  return useQuery({
    queryKey: ["leads", { orderId }], enabled: Boolean(orderId),
    queryFn: async () => { const { data, error } = await supabase.from("leads").select("*, lead_identities(id, first_name, last_name, phone, email, anonymized_at), products(sku, name), clients!leads_client_id_fkey(id, legal_name)").eq("order_id", orderId!).order("received_at"); if (error) throw error; return data as unknown as LeadRow[]; },
  });
}
export function useDisputes(opts: { orderId?: string; clientId?: string } = {}) {
  return useQuery({
    queryKey: ["disputes", opts],
    queryFn: async () => {
      let q = supabase.from("disputes").select("*, clients(id, legal_name), orders(id, number)").order("opened_at", { ascending: false });
      if (opts.orderId) q = q.eq("order_id", opts.orderId);
      if (opts.clientId) q = q.eq("client_id", opts.clientId);
      const { data, error } = await q; if (error) throw error; return data;
    },
  });
}
export function useDispute(id: string | undefined) {
  return useQuery({
    queryKey: ["dispute", id], enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase.from("disputes").select("*, clients(id, legal_name), orders(id, number), dispute_leads(*, leads!dispute_leads_lead_id_fkey(*, lead_identities(id, first_name, last_name, phone, email, anonymized_at), products(sku, name)))").eq("id", id!).single();
      if (error) throw error; return data;
    },
  });
}

export const personName = (i: LeadRow["lead_identities"]) => i?.anonymized_at ? "(anonymisé)" : [i?.first_name, i?.last_name].filter(Boolean).join(" ") || "—";
